import { Dialog, DialogContent, DialogTitle } from '@mui/material';

import { CncReportLocal, Project, ProrataInformation } from '@gameonly/core';
import { useTranslation } from 'react-i18next';

import { Box, Step } from '@mui/material';
import { useState } from 'react';
import CompleteQuestion from './CompleteQuestion';
import CreateLocalInternal from './CreateLocalInternal';
import CreateLocalPartner from './CreateLocalPartner';
import LocalType from './LocalType';
import ProjectSelection from './ProjectSelection';
import ProrataConfiguration from './ProrataConfiguration';
import useUser, { isUserSubscribed } from '../authentication/context/useUser';

interface CreateLocalProps {
  open: boolean;
  onClose: () => void;
  onSave: (local: Partial<CncReportLocal>) => void;
  loading: boolean;
}

type StepKey =
  | 'LOCAL_TYPE'
  | 'COMPLETE_QUESTION'
  | 'FINAL_INTERNAL_COMPLETE'
  | 'PRORATA_CONFIGURATION'
  | 'PRORATA_PROJECT_SELECTION'
  | 'FINAL_PARTNER';

type Step = {
  label: string;
  next: ((input: string) => StepKey | null) | (() => null);
};

const STEP_TREE: Record<StepKey, Step> = {
  LOCAL_TYPE: {
    label: 'cnc-reports:create-modal-local.local-type',
    next: (localType: string) =>
      localType === 'Interne' ? 'COMPLETE_QUESTION' : 'FINAL_PARTNER',
  },
  COMPLETE_QUESTION: {
    label: 'cnc-reports:create-modal-local.complete-question',
    next: (isComplete: string) =>
      isComplete === 'complete'
        ? 'FINAL_INTERNAL_COMPLETE'
        : 'PRORATA_PROJECT_SELECTION',
  },
  PRORATA_PROJECT_SELECTION: {
    label: 'cnc-reports:create-modal-local.local-selection',
    next: () => 'PRORATA_CONFIGURATION' as StepKey,
  },
  PRORATA_CONFIGURATION: {
    label: 'cnc-reports:create-modal-internal.prorata-config',
    next: () => null,
  },
  FINAL_INTERNAL_COMPLETE: {
    label: 'cnc-reports:create-modal-local.local-selection',
    next: () => null,
  },
  FINAL_PARTNER: {
    label: 'cnc-reports:create-modal-local.details',
    next: () => null,
  },
};

const CreateLocal = ({ open, onSave, onClose, loading }: CreateLocalProps) => {
  const { t } = useTranslation(['cnc-reports', 'common', 'model']);
  const [localType, setLocalType] = useState<string>('');
  const [isComplete, setIsComplete] = useState<string>('');
  const [activeStep, setActiveStep] = useState<StepKey>('LOCAL_TYPE');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const { user } = useUser();

  const handleClose = () => {
    onClose();
  };

  const handleNext = () => {
    if (activeStep) {
      const step = STEP_TREE[activeStep];
      let nextStep: StepKey | null = null;

      if (
        activeStep === 'COMPLETE_QUESTION' &&
        localType === 'Interne' &&
        isComplete
      ) {
        nextStep = step.next(isComplete);
      } else {
        nextStep = step.next(localType);
      }

      if (nextStep) {
        setActiveStep(nextStep);
      }
    }
  };

  const handleSaveWithProrata = async (
    prorataInformation: ProrataInformation,
  ) => {
    if (!selectedProject) return;
    const local: Partial<CncReportLocal> = {
      id: selectedProject._id,
      type: 'internal',
      name: prorataInformation.projectSourceName,
      prorata: prorataInformation.prorata,
      validated: false,
    };
    await onSave(local);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('cnc-reports:create-modal-local.title')}</DialogTitle>
      <DialogContent>
        <p className="hxb">{t(STEP_TREE[activeStep].label)}</p>

        {activeStep === 'LOCAL_TYPE' && (
          <Box mt={2}>
            <LocalType
              localType={localType}
              setLocalType={setLocalType}
              onClose={handleClose}
              onNext={handleNext}
            />
          </Box>
        )}

        {activeStep === 'COMPLETE_QUESTION' && localType === 'Interne' && (
          <Box mt={2}>
            <CompleteQuestion
              subscribed={isUserSubscribed(user)}
              isComplete={isComplete}
              setIsComplete={setIsComplete}
              onClose={handleClose}
              onNext={handleNext}
              loading={false}
              onNextLabel={t('common:next')}
            />
          </Box>
        )}

        {activeStep === 'FINAL_INTERNAL_COMPLETE' && (
          <Box mt={2}>
            <CreateLocalInternal onClose={handleClose} onSave={onSave} />
          </Box>
        )}

        {activeStep === 'PRORATA_PROJECT_SELECTION' && (
          <Box mt={2}>
            <ProjectSelection
              onClose={handleClose}
              onNext={handleNext}
              setSelectedProject={setSelectedProject}
            />
          </Box>
        )}

        {activeStep === 'PRORATA_CONFIGURATION' && selectedProject && (
          <Box mt={2} sx={{ marginTop: 0 }}>
            <ProrataConfiguration
              project={selectedProject}
              onClose={handleClose}
              onSave={handleSaveWithProrata}
              loading={loading}
            />
          </Box>
        )}

        {activeStep === 'FINAL_PARTNER' && (
          <Box mt={2}>
            <CreateLocalPartner onClose={handleClose} onSave={onSave} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateLocal;
